import { gl } from '../../Engine';
import { Uniform } from './uniforms/Uniform';

/**
 * Interface that makes it easier to instantiate and work with WebGL shader programs.
 *
 * Shader programs in WebGL consist of two shader stages, VERTEX_SHADER and FRAGMENT_SHADER.
 *
 * VERTEX_SHADER transforms the input vertex positions from their original object space (local coordinates)
 * to the desired screen space (clip coordinates) for rendering on a 2D screen.
 * This transformation involves applying various mathematical operations such as translations, rotations, scaling, and perspective projection.
 *
 * In addition to position transformations, a VERTEX_SHADER can also modify other vertex attributes such as color,
 * texture coordinates, normals, or any user-defined attributes associated with the vertices.
 * This allows for various visual effects and deformations to be applied to the geometry of the model.
 *
 * To pass vertex data to VERTEX_SHADER we need to somehow tell WebGL where to look for those values in the WebGL memory.
 * In VERTEX_SHADER sourcecode input-variables are defined using 'in vec3 position;' format.
 * 'in' tells the shader that the variable should be read outside the render program.
 * 'vec3' is the data type. For more datatypes refer to glsl documentation https://www.khronos.org/opengl/wiki/Data_Type_(GLSL)
 * 'position' is the name of the input variable. Which should be provided in the 'inVariables'-parameter
 * When using the ShaderProgram class we do this by providing the variable names in correct order to constructor's inVariables-parameter.
 *
 * We can also provide data using UniformVariables that stay constant for the whole duration of the single gl.drawElements or gl.drawArrays-
 * render-cycle. Uniform variables can be used in both VERTEX_SHADER and FRAGMENT_SHADER stage.
 *
 * To define Uniform variable one should create Uniform object and
 * at the time of linking the ShaderProgram, call shader.storeUniformLocations()-method with all the uniform variables needed.
 * Then we can load values to those UniformVariables right before the gl.drawElements/gl.drawArrays method calls.
 */
export class ShaderProgram {
    /**
     * Reference to WebGLProgram
     */
    protected readonly programID: WebGLProgram;

    /**
     * Constructor that will create a ShaderProgram from VERTEX_SOURCE and FRAGMENT_SOURCE with given input variables.
     * It compiles the Vertex program, Fragment program, links those and makes the program usable.
     * @throws if ShaderProgram creation fails, or if propgram linking fails
     * @param vertexContent Content of the Vertex shader stage
     * @param fragmentContent Content of the Fragment shader stage
     * @param inVariables The input variables of the Vertex shader stage
     */
    constructor(
        vertexContent: string,
        fragmentContent: string,
        ...inVariables: string[]
    ) {
        // Track the GL objects locally so a failure at any stage —
        // shader compile, program creation, link, or validate — can
        // release everything already created before rethrowing, rather
        // than leaking the partial allocations.
        let vertexShaderID: WebGLShader | undefined;
        let fragmentShaderID: WebGLShader | undefined;
        let programID: WebGLProgram | undefined;
        try {
            vertexShaderID = this.loadShader(vertexContent, gl.VERTEX_SHADER);
            fragmentShaderID = this.loadShader(
                fragmentContent,
                gl.FRAGMENT_SHADER
            );

            programID = gl.createProgram() || undefined;
            if (!programID) {
                throw new Error("Couldn't get valid shader program ID");
            }
            this.programID = programID;
            gl.attachShader(this.programID, vertexShaderID);
            gl.attachShader(this.programID, fragmentShaderID);

            this.bindAttributes(inVariables);

            gl.linkProgram(this.programID);

            if (!gl.getProgramParameter(this.programID, gl.LINK_STATUS)) {
                const info = gl.getProgramInfoLog(this.programID);
                throw new Error(`Could not compile program:\n\n${info}`);
            }

            // Point each sampler at its texture unit before validating. Every
            // sampler starts on unit 0, and a program whose samplers differ in
            // type is invalid while they share one — so a program with more
            // than one kind of sampler cannot be validated until they have
            // been separated.
            this.assignSamplerUnits(this.programID);

            // Validate the program
            gl.validateProgram(this.programID);
            if (!gl.getProgramParameter(this.programID, gl.VALIDATE_STATUS)) {
                const info = gl.getProgramInfoLog(this.programID);
                throw new Error(`Program validation failed:\n\n${info}`);
            }

            // The linked program contains the linked binary; the shader
            // stage objects are compile-time intermediates only. Delete
            // them now (GL defers the actual release of attached shaders
            // until the program is deleted) so deleteProgram frees
            // everything deterministically instead of the stages waiting
            // on garbage collection of their JS wrappers.
            gl.deleteShader(vertexShaderID);
            gl.deleteShader(fragmentShaderID);
        } catch (e) {
            if (programID) {
                gl.deleteProgram(programID);
            }
            if (vertexShaderID) {
                gl.deleteShader(vertexShaderID);
            }
            if (fragmentShaderID) {
                gl.deleteShader(fragmentShaderID);
            }
            throw e;
        }
    }

    /**
     * Method to bind the attributes of the VertexShader stage
     * @param inVariables Input variables of the VertexShader that should be provided with variable data
     */
    private bindAttributes(inVariables: string[]): void {
        if (this.programID) {
            for (let i = 0; i < inVariables.length; ++i) {
                gl.bindAttribLocation(this.programID, i, inVariables[i]);
            }
        }
    }

    /**
     * Texture unit each of this program's samplers reads from, as uniform
     * name to unit. The default binds none, which is right for a program with
     * at most one sampler: a lone sampler is correct on the unit 0 it already
     * defaults to.
     *
     * Called from the base constructor, so an override must return a literal
     * and must not read the subclass's own fields — they are not initialised
     * yet.
     */
    protected samplerUnits(): ReadonlyArray<readonly [string, number]> {
        return [];
    }

    /** Assigns {@link samplerUnits} on the freshly linked program. */
    private assignSamplerUnits(programID: WebGLProgram): void {
        const units = this.samplerUnits();
        if (units.length === 0) {
            return;
        }

        const previous = gl.getParameter(gl.CURRENT_PROGRAM);
        gl.useProgram(programID);
        for (const [name, unit] of units) {
            const location = gl.getUniformLocation(programID, name);
            if (!location) {
                throw new Error(
                    `Shader program declares a texture unit for sampler ` +
                        `"${name}", which the linked program does not have.`
                );
            }
            gl.uniform1i(location, unit);
        }
        gl.useProgram(previous);
    }

    /**
     * Method to set the ShaderProgram as active.
     * Only one shader can be active at any given time. This is a limitation of the WebGL context.
     */
    public start(): void {
        if (this.programID) {
            gl.useProgram(this.programID);
        }
    }

    /**
     * Method to set null as an active program
     */
    public stop(): void {
        gl.useProgram(null);
    }

    /**
     * Method to link the Uniform variables to Uniform's introduced in the shader code.
     * @param uniforms Uniform variables to be linked
     */
    protected storeUniformLocations(...uniforms: Uniform[]): void {
        if (this.programID) {
            try {
                for (let i = 0; i < uniforms.length; ++i) {
                    uniforms[i].storeUniformLocation(this.programID);
                }
            } catch (e) {
                // Subclass constructors call this after the base
                // constructor has already allocated the program; a
                // failed lookup (name mismatch, lost context) aborts
                // that constructor, so the program must be released
                // here or it leaks.
                gl.deleteProgram(this.programID);
                throw e;
            }
        }
    }

    /**
     * Method to load and compile a single shader stage
     * @throws if Shader creation or compilation fails
     * @param shaderContent The content of the shader stage
     * @param shaderType Type of the shader i.e. gl.VERTEX_SHADER or gl.FRAGMENT_SHADER
     * @returns reference to that Shader stage in WebGL memory
     */
    private loadShader(shaderContent: string, shaderType: number): WebGLShader {
        const shaderID = gl.createShader(shaderType);
        if (!shaderID) {
            throw new Error("Couldn't get valid shader ID");
        }
        gl.shaderSource(shaderID, shaderContent);
        gl.compileShader(shaderID);

        // Judge success by COMPILE_STATUS, not by the info log: drivers
        // may attach warnings to a successfully compiled shader, and
        // those must not fail the construction. The log is only the
        // error message.
        if (!gl.getShaderParameter(shaderID, gl.COMPILE_STATUS)) {
            const info = gl.getShaderInfoLog(shaderID);
            // Release the shader before failing so a compile error does
            // not leak it.
            gl.deleteShader(shaderID);
            throw new Error(`Could not compile shader:\n\n${info}`);
        }

        return shaderID;
    }

    /**
     * Method to delete/de-allocate shader from the WebGL memory.
     * This should be called whenever the shader is no longer needed.
     */
    public delete(): void {
        if (this.programID) {
            gl.deleteProgram(this.programID);
        }
    }

    public getProgramID(): WebGLProgram {
        return this.programID;
    }
}
